import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './Browser.module.css';
import { useWindowManager, WindowId } from '../Window/WindowManager';
import { useWindowResize } from '../Window/useWindowResize';
import { computeWindowDefaults } from '../Window/windowLayout';
import { ResizeHandles } from '../Window/ResizeHandles';
import { WindowControls } from '../Window/WindowControls';
import { GlobeIcon, GithubIcon, LinkedinIcon, MailIcon, LockIcon } from '../icons';
import { GitHubPage, LinkedInPage, EmailPage } from './pages';
import type { ReactNode } from 'react';

interface Tab {
  id: string;
  label: string;
  url: string;
  href: string;
  favicon: ReactNode;
}

const DEFAULT_TABS: Tab[] = [
  { id: 'github', label: 'GitHub', url: 'https://github.com/nickbozentko', href: 'https://github.com/nickbozentko', favicon: <GithubIcon /> },
  { id: 'linkedin', label: 'LinkedIn', url: 'https://linkedin.com/in/nicholas-bozentko', href: 'https://linkedin.com/in/nicholas-bozentko/', favicon: <LinkedinIcon /> },
  // { id: 'email', label: 'Email', url: 'mailto:Nick', href: '', favicon: <MailIcon /> },
];

export function Browser() {
  const [activeTab, setActiveTab] = useState('github');
  const [tabs] = useState<Tab[]>(DEFAULT_TABS);

  // Window manager
  const {
    zIndex,
    focus,
    close,
    minimize,
    maximize,
    isMinimized,
    isClosed,
    isMaximized,
  } = useWindowManager(WindowId.Browser, 'Browser', <GlobeIcon />);

  // Window resize
  const { size, position, setPosition, isResizing, handleResizeStart } = useWindowResize(
    { width: 640, height: 480 },
    { x: 760, y: 25 },
    { minWidth: 300, minHeight: 180, computeDefaults: computeWindowDefaults(WindowId.Browser) }
  );

  // Browser dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const browserRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!browserRef.current) return;
    if (isMaximized) return; // Don't allow dragging when maximized
    focus();
    // Don't drag if clicking on a tab
    if ((e.target as HTMLElement).closest(`.${styles.browserTab}`)) return;

    const rect = browserRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
  }, [focus, isMaximized]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const desktopEl = document.querySelector('[data-desktop]') as HTMLElement;
      if (!desktopEl) return;

      const desktopRect = desktopEl.getBoundingClientRect();
      const browserEl = browserRef.current;
      if (!browserEl) return;

      const browserWidth = browserEl.offsetWidth;
      const browserHeight = browserEl.offsetHeight;

      let newX = e.clientX - desktopRect.left - dragOffset.current.x;
      let newY = e.clientY - desktopRect.top - dragOffset.current.y;

      // Constrain to desktop bounds (account for dock)
      const dockHeight = 56;
      newX = Math.max(0, Math.min(newX, desktopRect.width - browserWidth));
      newY = Math.max(0, Math.min(newY, desktopRect.height - browserHeight - dockHeight));

      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, setPosition]);

  // Close animation
  const [isClosing, setIsClosing] = useState(false);

  // Handle button clicks
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClosing(true);
  };

  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === styles['windowClose']) {
      setIsClosing(false);
      close();
    }
  }, [close]);

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    minimize();
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximize();
  };

  // Don't render if closed or minimized
  if (isClosed || isMinimized) {
    return null;
  }

  const currentTab = tabs.find(t => t.id === activeTab) || tabs[0];

  // Calculate style based on maximized state
  const windowStyle = isMaximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: 'calc(100% - 56px)', // Account for dock
        zIndex,
      }
    : {
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex,
      };

  return (
    <div
      ref={browserRef}
      className={`${styles.browser} ${isDragging || isResizing ? styles.dragging : ''} ${isMaximized ? styles.maximized : ''} ${isClosing ? styles.closing : ''}`}
      style={windowStyle}
      onPointerDown={focus}
      onAnimationEnd={handleAnimationEnd}
    >
      {!isMaximized && <ResizeHandles onResizeStart={handleResizeStart} />}
      <div
        className={styles.browserHeader}
        onPointerDown={handlePointerDown}
      >
        <WindowControls
          onClose={handleClose}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
        />
        <div className={styles.browserTabs}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.browserTab} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className={styles.tabFavicon}>{tab.favicon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={styles.browserAddressBar}>
        <span className={styles.addressIcon}><LockIcon /></span>
        <a className={styles.addressUrl} href={currentTab.href} target="_blank" rel="noopener noreferrer">{currentTab.url}</a>
      </div>
      <div className={styles.browserBody}>
        {activeTab === 'github' && <GitHubPage />}
        {activeTab === 'linkedin' && <LinkedInPage />}
        {/* {activeTab === 'email' && <EmailPage />} */}
      </div>
    </div>
  );
}
